import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import styles from './time-picker.module.scss';
import { useOutsideClick } from '../../hooks';
import { IconButton } from '../icon-button';
import { InputMessage } from '../input-message';
import { TimePickerFlyout } from './_flyout';

export type TimePickerItem = { value: string; onClick: () => void };

export type TimePickerProps = {
  value: string;
  label: string;
  id: string;
  items: TimePickerItem[];
  resetButton: { ariaLabel: string; onClick: () => void };
  message?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  className,
  items,
  disabled,
  value,
  label,
  resetButton,
  id,
  message,
  isInvalid
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [activeOption, setActiveOption] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const classes = classnames(
    styles.timePickerOuter,
    { [styles.open]: open },
    { [styles.hasValue]: value },
    { [styles.disabled]: disabled },
    { [styles.invalid]: isInvalid },
    className
  );

  const messageId = `${id}-message`;

  const handleClose = () => {
    setOpen(false);
    setActiveOption(undefined);
  };

  useOutsideClick(outerRef, handleClose);

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.classList.add('no-scroll');

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const el = ref.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled) {
        return;
      }

      if (!open) {
        if (['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(e.key)) {
          setActiveOption(0);
          return setOpen(true);
        }
      }

      if (activeOption === undefined) {
        // When mouse opens the flyout, a following arrow down will set the activeOption to first item
        if (e.key === 'ArrowDown') {
          return setActiveOption(0);
        }

        // When mouse opens the flyout, a following arrow up will set the activeOption to last item
        if (e.key === 'ArrowUp') {
          return setActiveOption(items.length - 1);
        }
      }

      if (activeOption !== undefined) {
        switch (e.key) {
          case 'ArrowDown':
            if (activeOption === items.length - 1) {
              return;
            }
            return setActiveOption(activeOption + 1);
          case 'ArrowUp':
            if (activeOption === 0) {
              return;
            }
            return setActiveOption(activeOption - 1);
          case 'Escape':
            return handleClose();
        }

        if (['Tab', 'Enter', ' '].includes(e.key)) {
          setOpen(false);
          return items[activeOption].onClick();
        }
      }
    };

    el.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <div className={classes} ref={outerRef}>
      <div data-testid="combo-button-component" ref={ref}>
        <label id={`${id}-label`} className={styles.label}>
          {label}
        </label>
        <div
          className={styles.timePicker}
          id={id}
          aria-controls={`${id}-listbox`}
          role="combobox"
          aria-invalid={isInvalid}
          aria-disabled={disabled}
          aria-labelledby={`${id}-label`}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-describedby={message ? messageId : undefined}
          onClick={() => setOpen(!open)}
          tabIndex={0}
          aria-activedescendant={activeOption || activeOption === 0 ? `${id}-option-${activeOption}` : ''}
        >
          {value}
        </div>
        <TimePickerFlyout
          items={items}
          open={open}
          activeOption={activeOption}
          value={value}
          id={id}
          closeFlyoutClick={() => setOpen(false)}
        />
        {message && (
          <InputMessage
            isError={isInvalid}
            message={message}
            disabled={disabled}
            className={styles.inputMessage}
            id={messageId}
          />
        )}
        {open && <div className={styles.layer} />}
      </div>
      {value && (
        <IconButton
          ariaLabel={resetButton.ariaLabel}
          variant="ghost"
          iconName="close-with-circle--small"
          className={styles.iconButton}
          size="200"
          onClick={() => {
            resetButton.onClick();
            setOpen(false);
          }}
        />
      )}
    </div>
  );
};
